import React from "react";
import i18n from "i18next";
import styled from "styled-components";
import { connect, ReactReduxContext } from "react-redux";
import { DialogOverlay, Dialog } from "components/dialog/DialogOverlay";
import {
  capitalizeStringWithSpaces,
  uploadAiImportDocument,
  currentPortfolioSelector,
  getAiImportExtractedData,
  userPreferencesSelector,
  aiSupportedFileTypesSelector,
  rescanAiImportDocument,
  apiErrorCodes,
  fundScheduleTypes,
  custodianSelector,
  canShowCommittedCapital,
  lastLinkSource
} from "@kubera/common";
import { withRouter } from "@kubera/common";
import AiDocumentImportIcon from "assets/images/ai_document_import.svg?react";
import AiDocumentImportErrorIcon from "assets/images/ai_document_import_error.svg?react";
import DragDropInput from "components/inputs/DragDropInput";
import FileUploadInput from "components/inputs/FileUploadInput";
import SecondaryButton from "components/button/SecondaryButton";
import FancyLoader from "components/loader/FancyLoader";
import { getFileExtension, getFileNameWithoutExtension } from "utilities/FileUtils";
import ConfirmationDialog from "components/dialog/ConfirmationDialog";
import { hashParams, modalValues } from "routes";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import AddModal from "components/assets/AddModal";
import AiCustodianImportComponent from "./AiCustodianImportComponent";
import AiCashflowImportComponent from "./AiCashflowImportComponent";
import AiFundScheduleImportComponent from "./AiFundScheduleImportComponent";
import {
  aiImportContext,
  aiImportSubContext,
  buildLastLinkMetadata,
  detectArchiveHoldings,
  buildAccountLocationMap
} from "./AiImportExports";
import { TrackAnalytics } from "utilities/TrackAnalytics";
import { ANALYTICS } from "analytics";
import {
  aiSyncReviewEventType,
  broadcastAiSyncReviewEvent
} from "components/ai_sync_extension/aiSyncReviewBroadcastChannel";

const importTabs = {
  CUSTODIAN: "CUSTODIAN",
  CASHFLOW: "CASHFLOW",
  CAPITAL_CALL: "CAPITAL_CALL",
  DISTRIBUTION: "DISTRIBUTION"
};

const FILE_UNSUPPORTED_ERROR = "FILE_UNSUPPORTED_ERROR";

const MAX_FILE_SIZE = 1024 * 1024 * 25;

const ImportDialog = styled(Dialog)`
  position: relative;
  width: 1130px;
  min-height: 600px;
  display: flex;
  align-items: stretch;
  margin-top: 74px;
  justify-content: center;
`;

const Container = styled.div`
  position: relative;
  display: flex;
  margin: 35px;
  flex-direction: column;
  justify-content: flex-start;
  align-items: left;
  flex: 1;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-end;
`;

const Title = styled.div`
  max-width: 666px;
  font-size: 22px;
  font-weight: 700;
  line-height: 28.6px;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ErrorPreview = styled.div`
  width: 666px;
  margin-top: 15px;
  font-weight: 700;
  font-size: 14px;
  line-height: 21px;
  letter-spacing: 0%;
  text-decoration: underline;
  text-decoration-style: solid;
  text-decoration-offset: Auto;
  text-decoration-thickness: Auto;
  text-decoration-skip-ink: auto;
  color: #0074fc;
  cursor: pointer;
`;

const Preview = styled.div`
  margin-left: 5px;
  margin-bottom: 6px;
  font-weight: 400;
  font-size: 11px;
  line-height: 13.31px;
  letter-spacing: 0%;
  text-decoration: underline;
  text-decoration-style: solid;
  text-decoration-offset: Auto;
  text-decoration-thickness: Auto;
  text-decoration-skip-ink: auto;
  color: #0074fc;
  text-transform: uppercase;
  cursor: pointer;
`;

const Description = styled.div`
  max-width: ${props => props.maxWidth || "630px"};
  flex: 1;
  margin-top: 20px;
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;
  text-align: left;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
`;

const AiDocumentImportIconComponent = styled(AiDocumentImportIcon)`
  position: absolute;
  right: 25px;
  top: 25px;
  width: 98px;
  height: 98px;
  path {
    fill: ${props => props.theme.svgDefaultColor};
  }
`;

const AiDocumentImportErrorIconComponent = styled(AiDocumentImportErrorIcon)`
  position: absolute;
  right: 25px;
  top: 25px;
  width: 98px;
  height: 98px;
  path {
    fill: ${props => props.theme.svgDefaultColor};
  }
`;

const DocumentUploadContainer = styled(DragDropInput)`
  width: 100%;
  height: 100%;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  margin: 25px 25px 50px 25px;
`;

const CommandArea = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background: ${props => props.theme.documentsDetailsCommandAreaBG};
  display: flex;
  justify-content: center;
  visibility: ${props => (props.isDragging === false ? "visible" : "hidden")};
`;

const UploadButtonContainer = styled.div`
  margin: ${props => props.margin};
  width: fit-content;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const UploadButton = styled(FileUploadInput)`
  top: 168px;
  left: 0;
  display: flex;
  justify-content: center;
  padding-left: 20px;
  padding-right: 20px;
  min-width: 100px;
  height: 44px;
  font-style: normal;
  font-weight: bold;
  font-size: 12px;
  line-height: 15px;
  font-feature-settings: "ss01" on;
  color: #ffffff;
  background: #000000;
  cursor: pointer;
  outline: 0;
  border: 0;
`;

const UploadHint = styled.div`
  margin-top: 8px;
  white-space: pre-wrap;
  display: flex;
  justify-content: center;
  font-style: normal;
  font-weight: normal;
  font-size: 11px;
  line-height: 13px;
  font-feature-settings: "ss01" on;
  color: rgba(0, 0, 0, 0.5);
`;

const DropAreaIndicator = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  background: rgba(252, 252, 252, 0.97);
  border: 2px dashed rgba(0, 0, 0, 0.2);
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  visibility: ${props => (props.isDragging === true ? "visible" : "hidden")};
`;

const DropAreaHint = styled.div`
  align-self: center;
  white-space: pre-wrap;
  font-size: 11px;
  font-weight: 800;
  line-height: 13.31px;
  text-align: center;
  opacity: 0.5;
  text-transform: uppercase;
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: flex-end;
`;

const SubTitleContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const AbortButton = styled(SecondaryButton)`
  min-width: 45px;
  max-width: 60px;
  padding-left: 20px;
  padding-right: 20px;
  height: 30px;
  font-size: 9px;
  text-transform: uppercase;
  opacity: 0.5;
  border-color: rgba(0, 0, 0, 0.5);
`;

const SubTitle = styled.div`
  margin-bottom: 4px;
  font-size: 13px;
  font-weight: 600;
  line-height: 15.73px;
`;

const UploadLoader = styled(FancyLoader)`
  flex: 1;
  margin-top: 10px;
`;

const ExtractedDataDescription = styled.div`
  margin-top: 15px;
  color: #7a7a7a;
  white-space: pre-wrap;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  text-align: left;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  margin-top: 20px;
`;

const RescanButton = styled(SecondaryButton)`
  min-width: 50px;
  margin-left: 20px;
  padding-left: 20px;
  padding-right: 20px;
`;

const ImportTabs = styled(Tabs)`
  margin-top: 25px;
  display: flex;
  flex: 1;
  flex-direction: column;
  position: relative;
  white-space: nowrap;
`;

const ImportTabList = styled(TabList)`
  display: flex;
  list-style-type: none;
  margin: 0;
  padding-inline-start: 0;
  padding-bottom: 8px;
`;

const ImportTab = styled(Tab)`
  font-style: normal;
  font-weight: bold;
  font-size: 10px;
  line-height: 12px;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.35);
  padding-bottom: 8px;
  margin-right: 25px;
  border: 0;
  cursor: pointer;
  z-index: 100;

  &.is-selected {
    color: black;
    border-bottom: 2px solid rgba(0, 0, 0, 1);
  }
`;

const ImportTabPanel = styled(TabPanel)`
  margin-top: -10px;
  display: none;
  border-top: 2px solid rgba(0, 0, 0, 0.1);
  box-sizing: border-box;
  &.is-selected {
    display: flex;
    flex: 1;
  }
`;

const NoResultsMessage = styled.div`
  margin-top: 20px;
  font-weight: 400;
  font-size: 14px;
  line-height: 200%;
  letter-spacing: 0%;
  white-space: pre-line;
`;

class AiDocumentImportComponent extends React.Component {
  static contextType = ReactReduxContext;

  static show = (
    history,
    location,
    targetSheetId = undefined,
    targetCustodianId = undefined,
    context = undefined,
    subContext = aiImportSubContext.MANUAL,
    shouldReplace = false
  ) => {
    let hash = `${hashParams.MODAL}=${modalValues.AI_DOCUMENT_IMPORT}`;
    if (targetSheetId) {
      hash += `&${hashParams.SHEET_ID}=${targetSheetId}`;
    }
    if (targetCustodianId) {
      hash += `&${hashParams.ID}=${targetCustodianId}`;
    }
    if (context) {
      hash += `&${hashParams.CONTEXT}=${context}`;
    }
    if (subContext) {
      hash += `&${hashParams.SUB_CONTEXT}=${subContext}`;
    }

    if (shouldReplace) {
      history.replace({
        ...location,
        hash: hash
      });
    } else {
      history.push({
        ...location,
        hash: hash
      });
    }
  };

  constructor(props) {
    super(props);

    this.state = {
      isDragging: false,
      isUploading: !!props.documentId && !props.files,
      files: null,
      error: null,
      dataExtracted: null,
      documentApiData: null,
      dataExtractedApiData: null,
      isDataPartial: false,
      tabIndex: 0,
      itemsPendingImportMap: {},
      isImporting: false
    };

    this.tabs = null;
    this.accountLocationResult = null;
    this.tabSyncFns = {};
    this.accountLocationsListeners = {};
    this.combinedSyncInProgress = false;
    this.isSyncCompletedTracked = false;

    this.handleOverlayDismiss = this.handleOverlayDismiss.bind(this);
    this.handleDragStart = this.handleDragStart.bind(this);
    this.handleDragStop = this.handleDragStop.bind(this);
    this.handleFileUpload = this.handleFileUpload.bind(this);
    this.handleRescanButtonClick = this.handleRescanButtonClick.bind(this);
    this.handleTabPendingItemsChange = this.handleTabPendingItemsChange.bind(this);
    this.handleTabImportingStatusChange = this.handleTabImportingStatusChange.bind(this);
    this.registerTabSyncFn = this.registerTabSyncFn.bind(this);
    this.registerAccountLocationsListener = this.registerAccountLocationsListener.bind(this);
    this.runCombinedSync = this.runCombinedSync.bind(this);
    this.handleAccountLocationsChange = this.handleAccountLocationsChange.bind(this);
    this.setTab = this.setTab.bind(this);

    if (!this.props.files === false) {
      setTimeout(() => {
        this.handleFileUpload(this.props.files);
      }, 10);
    }
  }

  componentDidMount() {
    if (this.props.documentId && !this.props.files) {
      this.loadExtractedDataForDocumentId(this.props.documentId);
    }
  }

  componentDidUpdate(oldProps) {
    if (oldProps.files !== this.props.files) {
      setTimeout(() => {
        this.handleFileUpload(this.props.files);
      }, 10);
    }
  }

  handleOverlayDismiss(showConfirmation = true, closeAddModal = false) {
    if (showConfirmation) {
      this.setState({ showDismissConfirmation: true });
    } else {
      if (closeAddModal !== true && !this.areAllTabsDone() && this.isReviewDataFlow() && this.props.documentId) {
        broadcastAiSyncReviewEvent(this.props.documentId, aiSyncReviewEventType.IMPORT_ABORTED);
      }
      if (closeAddModal) {
        AddModal.close();
      }
      setTimeout(() => {
        this.props.onDismiss();
        const goBackToPreviousModal =
          this.props.context === aiImportContext.CASHFLOW || this.props.context === aiImportContext.FUND_SCHEDULE;
        DialogOverlay.dismiss(this.props.history, this.props.location, goBackToPreviousModal);
      }, 50);
    }
  }

  handleDragStart() {
    this.setState({ isDragging: true });
  }

  handleDragStop() {
    this.setState({ isDragging: false });
  }

  handleExtractedDataApiResponse(apiData, isNewScan) {
    const isDataPartial = apiData.statusInfo === "MAX_TOKENS";
    const dataExtracted = apiData.dataExtracted;

    this.isSyncCompletedTracked = false;
    if (isNewScan) {
      TrackAnalytics({
        category: ANALYTICS.CATEGORY.USER,
        action: this.isReviewDataFlow()
          ? ANALYTICS.ACTION.WEB_SYNC_SCAN_COMPLETED
          : ANALYTICS.ACTION.FILE_IMPORT_SCAN_COMPLETED
      });
    }

    if (this.isReviewDataFlow()) {
      detectArchiveHoldings(
        this.context.store.getState(),
        dataExtracted,
        this.props.currentPortfolio,
        this.props.pageUrl
      );
    }

    if (!dataExtracted.account) {
      dataExtracted.account = { asset: [], debt: [] };
    }
    this.accountLocationResult = buildAccountLocationMap(
      this.context.store.getState(),
      dataExtracted.account,
      this.props.currentPortfolio,
      this.isReviewDataFlow() ? null : undefined,
      dataExtracted.institution,
      this.isReviewDataFlow()
    );

    this.tabs = this.getTabs(isDataPartial, dataExtracted);
    const itemsPendingImportMap = {};
    let defaultTabIndex = undefined;
    for (const [index, tab] of this.tabs.entries()) {
      itemsPendingImportMap[tab.key] = tab.items.filter(item => item.ignore !== true).length;
      if (defaultTabIndex === undefined && tab.items.length > 0) {
        defaultTabIndex = index;
      }
    }

    console.log(dataExtracted);

    this.setState({
      isUploading: false,
      tabIndex: defaultTabIndex,
      dataExtracted: dataExtracted,
      dataExtractedApiData: apiData,
      isDataPartial: isDataPartial,
      itemsPendingImportMap: itemsPendingImportMap
    });
  }

  async handleFileUpload(files) {
    if (!files === true || files.length === 0) {
      return;
    }
    AddModal.close();
    const blacklistedFileExtensions = ["xls", "xlsx"];
    const fileExtension = getFileExtension(files[0].name);
    if (
      files[0].size > MAX_FILE_SIZE ||
      !this.props.aiSupportedFileTypes.includes(files[0].type) ||
      blacklistedFileExtensions.includes(fileExtension)
    ) {
      this.setState({ isUploading: false, error: FILE_UNSUPPORTED_ERROR });
      return;
    }

    const context =
      this.props.context === aiImportContext.FUND_SCHEDULE ? aiImportContext.CASHFLOW : this.props.context;
    this.setState({ files, isUploading: true, error: null, dataExtracted: null, isDataPartial: false });
    this.props
      .uploadAiImportDocument(
        this.props.currentPortfolio.id,
        files[0],
        context,
        this.props.targetCustodianId,
        this.props.targetSheetId
      )
      .then(apiData => this.fetchAndHandleExtractedData(apiData))
      .catch(apiError => {
        console.log(apiError);
        this.setState({ isUploading: false, error: apiError });
      });
  }

  getFileName(fileName, removeExtension = false) {
    return capitalizeStringWithSpaces(removeExtension ? getFileNameWithoutExtension(fileName) : fileName);
  }

  isReviewDataFlow() {
    return !!this.props.documentId;
  }

  getHeaderTitle() {
    if (this.isReviewDataFlow() && this.state.dataExtractedApiData?.name) {
      return this.getFileName(this.state.dataExtractedApiData.name, true);
    }
    return this.state.files ? this.getFileName(this.state.files[0]?.name, true) : i18n.t("aiImport.scanTitle");
  }

  getSubtitle() {
    return this.isReviewDataFlow() ? i18n.t("aiSync") : i18n.t("aiImport");
  }

  handleRescanButtonClick(e) {
    this.setState({ isUploading: true, error: null, dataExtracted: null, isDataPartial: false });

    this.props
      .rescanAiImportDocument(this.state.documentApiData.id)
      .then(apiData => this.fetchAndHandleExtractedData(apiData))
      .catch(apiError => {
        console.log(apiError);
        this.setState({ isUploading: false, error: apiError });
      });
  }

  loadExtractedDataForDocumentId(documentId) {
    this.setState({ isUploading: true, error: null, dataExtracted: null, isDataPartial: false });
    this.fetchAndHandleExtractedData({ id: documentId }, false).catch(apiError => {
      this.setState({ isUploading: false, error: apiError });
    });
  }

  fetchAndHandleExtractedData(documentApiData, isNewScan = true) {
    this.setState({ documentApiData });
    return this.props
      .getAiImportExtractedData(documentApiData.id, this.props.targetCustodianId)
      .then(apiData => this.handleExtractedDataApiResponse(apiData, isNewScan))
      .catch(apiError => {
        if (isNewScan && apiError?.errorCode === apiErrorCodes.EMPTY_RESPONSE) {
          TrackAnalytics({
            category: ANALYTICS.CATEGORY.USER,
            action: this.isReviewDataFlow()
              ? ANALYTICS.ACTION.WEB_SYNC_SCAN_NO_ACCOUNTS
              : ANALYTICS.ACTION.FILE_IMPORT_SCAN_NO_ACCOUNTS
          });
        }
        throw apiError;
      });
  }

  handleTabPendingItemsChange(tabKey, itemsProcessed, closeDialogIfAllTabsDone = false) {
    const newItemsPendingImportMap = this.state.itemsPendingImportMap;
    newItemsPendingImportMap[tabKey] = closeDialogIfAllTabsDone
      ? 0
      : this.tabs.find(item => item.key === tabKey).items.length - itemsProcessed;
    this.setState({ itemsPendingImportMap: newItemsPendingImportMap });

    if (closeDialogIfAllTabsDone && !this.isSyncCompletedTracked) {
      this.isSyncCompletedTracked = true;
      TrackAnalytics({
        category: ANALYTICS.CATEGORY.USER,
        action: this.isReviewDataFlow()
          ? ANALYTICS.ACTION.WEB_SYNC_COMPLETED
          : ANALYTICS.ACTION.FILE_IMPORT_SYNC_COMPLETED
      });
    }

    if (closeDialogIfAllTabsDone) {
      if (this.areAllTabsDone()) {
        if (this.isReviewDataFlow() && this.props.documentId) {
          broadcastAiSyncReviewEvent(this.props.documentId, aiSyncReviewEventType.IMPORT_COMPLETED);
        }
        this.handleOverlayDismiss(false, true);
      }
    }
  }

  registerTabSyncFn(tabKey, fn) {
    if (fn) {
      this.tabSyncFns[tabKey] = fn;
    } else {
      delete this.tabSyncFns[tabKey];
    }
  }

  registerAccountLocationsListener(tabKey, fn) {
    if (fn) {
      this.accountLocationsListeners[tabKey] = fn;
    } else {
      delete this.accountLocationsListeners[tabKey];
    }
  }

  // The Assets tab owns the live account locations; the cash flow / fund schedule tabs derive their
  // targets from it. Push every change straight to those tabs so they follow a holding being moved
  // and learn once its account has been created (so they don't recreate it).
  handleAccountLocationsChange(locationMap) {
    const newCustodianIdByAccountId = {};
    for (const [id, location] of Object.entries(locationMap)) {
      if (location?.newCustodianId) {
        newCustodianIdByAccountId[id] = location.newCustodianId;
      }
    }
    for (const listener of Object.values(this.accountLocationsListeners)) {
      listener(locationMap, newCustodianIdByAccountId);
    }
  }

  // While a combined sync is running, individual tabs flipping their importing status off as each
  // of their flushes completes would re-enable the abort/close button mid-sequence. Keep importing
  // on until the whole combined sequence resolves (success closes the dialog; failure clears it).
  handleTabImportingStatusChange(isImporting) {
    if (this.combinedSyncInProgress && isImporting === false) {
      return;
    }
    this.setState({ isImporting });
  }

  // Confirm & Sync from a cash flow / fund schedule tab when its rows map to accounts being
  // created in this session: sync every tab. The accounts (custodian) tab syncs first; only once
  // its flush completes successfully (custodians created server-side) do the cash flow / fund
  // schedule tabs sync, so their cash flows attach to accounts that already exist. Each tab
  // self-closes the dialog once all tabs are done.
  runCombinedSync(options = {}) {
    this.combinedSyncInProgress = true;
    this.combinedSyncAbortCallback = options.onAbort;
    this.setState({ isImporting: true });

    const fireCashflowTabs = () => {
      for (const tabKey of [importTabs.CASHFLOW, importTabs.CAPITAL_CALL, importTabs.DISTRIBUTION]) {
        this.tabSyncFns[tabKey]?.({ bypassConfirmation: true, onError: () => this.abortCombinedSync() });
      }
    };

    const accountFn = this.tabSyncFns[importTabs.CUSTODIAN];
    if (!accountFn) {
      fireCashflowTabs();
      return;
    }

    accountFn({
      bypassConfirmation: true,
      onSynced: result => {
        // If any account failed to create server-side, don't attach cash flows to custodians that
        // don't exist — stop the sequence and re-enable the dialog so the user can retry.
        if (result?.custodian?.failed?.length > 0) {
          this.abortCombinedSync();
          return;
        }
        fireCashflowTabs();
      },
      onError: () => this.abortCombinedSync()
    });
  }

  abortCombinedSync() {
    this.combinedSyncInProgress = false;
    this.setState({ isImporting: false });
    this.combinedSyncAbortCallback?.();
    this.combinedSyncAbortCallback = null;
  }

  areAllTabsDone() {
    return Object.entries(this.state.itemsPendingImportMap).every(([_, value]) => value === 0);
  }

  isGenericError(error) {
    return error.errorCode !== apiErrorCodes.RATE_LIMIT_EXCEEDED && error.errorCode !== apiErrorCodes.EMPTY_RESPONSE;
  }

  isMaxTokensError(error) {
    return error?.errorCode === apiErrorCodes.MAX_TOKENS;
  }

  getErrorTitle(error) {
    if (this.state.error === FILE_UNSUPPORTED_ERROR) {
      return i18n.t("aiImport.fileNotSupportedTitle");
    }
    if (this.isMaxTokensError(error)) {
      return i18n.t("aiImport.maxTokensErrorTitle");
    }
    return this.isGenericError(error)
      ? i18n.t("aiImport.genericErrorTitle")
      : i18n.t("aiImport.talkToHumansErrorTitle");
  }

  getErrorDescription(error) {
    if (this.state.error === FILE_UNSUPPORTED_ERROR) {
      return i18n.t("aiImport.fileNotSupportedDesc");
    }
    if (this.isMaxTokensError(error)) {
      return i18n.t("aiImport.maxTokensErrorDesc");
    }
    const isPdfFile = this.state.files ? this.state.files[0]?.type === "application/pdf" : false;
    if (this.isGenericError(error)) {
      return isPdfFile ? i18n.t("aiImport.genericPdfErrorDesc") : i18n.t("aiImport.genericErrorDesc");
    }
    return isPdfFile ? i18n.t("aiImport.talkToHumansPdfErrorDesc") : i18n.t("aiImport.talkToHumansErrorDesc");
  }

  getTabs(isDataPartial, dataExtracted) {
    const isReviewDataFlow = this.isReviewDataFlow();
    const lastLinkMetadata = buildLastLinkMetadata(
      dataExtracted,
      this.props.pageUrl,
      isReviewDataFlow ? lastLinkSource.AI_SYNC : lastLinkSource.AI_IMPORT
    );
    const accountTab = {
      title: i18n.t("aiImportTab.custodian"),
      key: importTabs.CUSTODIAN,
      items: [...dataExtracted.account.asset, ...dataExtracted.account.debt],
      panelComponent: (
        <AiCustodianImportComponent
          tabKey={importTabs.CUSTODIAN}
          targetSheetId={this.props.targetSheetId}
          documentId={this.state.documentApiData.id}
          isReviewDataFlow={isReviewDataFlow}
          isDataPartial={isDataPartial}
          dataExtracted={dataExtracted.account}
          institution={dataExtracted.institution}
          lastLinkMetadata={lastLinkMetadata}
          accountLocationResult={this.accountLocationResult}
          registerSyncFn={this.registerTabSyncFn}
          onAccountLocationsChange={this.handleAccountLocationsChange}
          onRescan={this.handleRescanButtonClick}
          onPendingItemsChange={this.handleTabPendingItemsChange}
          onImportingStatusChange={this.handleTabImportingStatusChange}
        />
      )
    };
    const cashflowTab = {
      title: i18n.t("aiImportTab.cashflow"),
      key: importTabs.CASHFLOW,
      items: [...dataExtracted.cashFlow.cashIn, ...dataExtracted.cashFlow.cashOut],
      panelComponent: (
        <AiCashflowImportComponent
          tabKey={importTabs.CASHFLOW}
          targetCustodianId={this.props.targetCustodianId}
          documentId={this.state.documentApiData.id}
          isReviewDataFlow={isReviewDataFlow}
          isDataPartial={isDataPartial}
          dataExtracted={dataExtracted.cashFlow}
          lastLinkMetadata={lastLinkMetadata}
          accountData={dataExtracted.account}
          accountLocationMap={this.accountLocationResult.locationMap}
          newCustodianIdByAccountId={this.accountLocationResult.newCustodianIdByAccountId}
          registerSyncFn={this.registerTabSyncFn}
          registerAccountLocationsListener={this.registerAccountLocationsListener}
          onRunCombinedSync={this.runCombinedSync}
          onRescan={this.handleRescanButtonClick}
          onPendingItemsChange={this.handleTabPendingItemsChange}
          onImportingStatusChange={this.handleTabImportingStatusChange}
        />
      )
    };
    const capitalCallTab = {
      title: i18n.t("aiImportTab.capitalCalls"),
      key: importTabs.CAPITAL_CALL,
      items: dataExtracted.capitalCall.items,
      panelComponent: (
        <AiFundScheduleImportComponent
          tabKey={importTabs.CAPITAL_CALL}
          targetCustodianId={this.props.targetCustodianId}
          fundScheduleType={fundScheduleTypes.CAPITAL_CALL}
          documentId={this.state.documentApiData.id}
          isReviewDataFlow={isReviewDataFlow}
          isDataPartial={isDataPartial}
          dataExtracted={dataExtracted.capitalCall}
          lastLinkMetadata={lastLinkMetadata}
          accountData={dataExtracted.account}
          accountLocationMap={this.accountLocationResult.locationMap}
          newCustodianIdByAccountId={this.accountLocationResult.newCustodianIdByAccountId}
          registerSyncFn={this.registerTabSyncFn}
          registerAccountLocationsListener={this.registerAccountLocationsListener}
          onRunCombinedSync={this.runCombinedSync}
          onRescan={this.handleRescanButtonClick}
          onPendingItemsChange={this.handleTabPendingItemsChange}
          onImportingStatusChange={this.handleTabImportingStatusChange}
        />
      )
    };
    const distributionTab = {
      title: i18n.t("aiImportTab.distributions"),
      key: importTabs.DISTRIBUTION,
      items: dataExtracted.distributionSchedule.items,
      panelComponent: (
        <AiFundScheduleImportComponent
          tabKey={importTabs.DISTRIBUTION}
          targetCustodianId={this.props.targetCustodianId}
          fundScheduleType={fundScheduleTypes.DISTRIBUTION}
          documentId={this.state.documentApiData.id}
          isReviewDataFlow={isReviewDataFlow}
          isDataPartial={isDataPartial}
          dataExtracted={dataExtracted.distributionSchedule}
          lastLinkMetadata={lastLinkMetadata}
          accountData={dataExtracted.account}
          accountLocationMap={this.accountLocationResult.locationMap}
          newCustodianIdByAccountId={this.accountLocationResult.newCustodianIdByAccountId}
          registerSyncFn={this.registerTabSyncFn}
          registerAccountLocationsListener={this.registerAccountLocationsListener}
          onRunCombinedSync={this.runCombinedSync}
          onRescan={this.handleRescanButtonClick}
          onPendingItemsChange={this.handleTabPendingItemsChange}
          onImportingStatusChange={this.handleTabImportingStatusChange}
        />
      )
    };

    const isFundScheduleSupportedForTarget = this.props.targetCustodian ? this.props.canShowCommittedCapital : true;
    if (this.props.context === aiImportContext.ACCOUNT) {
      return [accountTab];
    } else if (
      this.props.context === aiImportContext.CASHFLOW ||
      this.props.context === aiImportContext.FUND_SCHEDULE
    ) {
      return [cashflowTab, ...(isFundScheduleSupportedForTarget ? [capitalCallTab, distributionTab] : [])];
    } else {
      return [accountTab, cashflowTab, ...(isFundScheduleSupportedForTarget ? [capitalCallTab, distributionTab] : [])];
    }
  }

  setTab(tabIndex) {
    this.setState({
      tabIndex
    });
  }

  getTitleBasedOnContext() {
    if (this.props.context === aiImportContext.CASHFLOW) {
      return i18n.t("aiImportCashflowContext.title");
    } else if (this.props.context === aiImportContext.FUND_SCHEDULE) {
      return i18n.t("aiImportFundScheduleContext.title");
    } else if (this.props.subContext === aiImportSubContext.STOCKS) {
      return i18n.t("aiImportStocksContext.title");
    } else if (this.props.subContext === aiImportSubContext.CRYPTO) {
      return i18n.t("aiImportCryptoContext.title");
    } else if (this.props.subContext === aiImportSubContext.ALL) {
      return i18n.t("aiImportAllContext.title");
    }
    return i18n.t("aiImportManualAccountContext.title");
  }

  getDescriptionBasedOnContext() {
    if (this.props.context === aiImportContext.CASHFLOW) {
      return i18n.t("aiImportCashflowContext.desc");
    } else if (this.props.context === aiImportContext.FUND_SCHEDULE) {
      return i18n.t("aiImportFundScheduleContext.desc");
    } else if (this.props.subContext === aiImportSubContext.STOCKS) {
      return i18n.t("aiImportStocksContext.desc");
    } else if (this.props.subContext === aiImportSubContext.CRYPTO) {
      return i18n.t("aiImportCryptoContext.desc");
    } else if (this.props.subContext === aiImportSubContext.ALL) {
      return i18n.t("aiImportAllContext.desc");
    }
    return i18n.t("aiImportManualAccountContext.desc");
  }

  render() {
    if (this.state.dataExtracted) {
      return (
        <DialogOverlay onDismiss={() => this.handleOverlayDismiss(this.areAllTabsDone() === false)}>
          <ImportDialog>
            <Container>
              <ContentContainer>
                <HeaderContainer>
                  <SubTitleContainer>
                    <SubTitle>{this.getSubtitle()}</SubTitle>
                    <Header>
                      <Title>{this.getHeaderTitle()}</Title>
                      {this.state.files && (
                        <Preview
                          onClick={e => {
                            const fileWindow = window.open(URL.createObjectURL(this.state.files[0]), "_blank");
                            const name = this.state.files[0].name;
                            fileWindow.addEventListener("load", function() {
                              fileWindow.document.title = name;
                            });
                          }}
                        >
                          {i18n.t("preview")}
                        </Preview>
                      )}
                    </Header>
                  </SubTitleContainer>
                  <AbortButton
                    isDisabled={this.state.isImporting}
                    onClick={() => this.handleOverlayDismiss(false)}
                    title={i18n.t("close")}
                  />
                </HeaderContainer>
                <ImportTabs
                  forceRenderTabPanel={true}
                  selectedTabClassName="is-selected"
                  selectedTabPanelClassName="is-selected"
                  selectedIndex={this.state.tabIndex}
                  onSelect={this.setTab}
                >
                  <ImportTabList>
                    {this.tabs.map((tab, index) => (
                      <ImportTab data-cy={"aiImportTab" + index} key={index}>
                        {`${tab.title} (${this.state.itemsPendingImportMap[tab.key]})`}
                      </ImportTab>
                    ))}
                  </ImportTabList>
                  {this.tabs.map((tab, index) => (
                    <ImportTabPanel key={`aiImportTabsPanel${index}`}>
                      {tab.items.length === 0 ? (
                        <NoResultsMessage>
                          {i18n
                            .t(this.isReviewDataFlow() ? "aiSync.noResultsOnWebpage" : "aiImport.noAccountResults")
                            .replace("%s1%", tab.title)}
                        </NoResultsMessage>
                      ) : (
                        tab.panelComponent
                      )}
                    </ImportTabPanel>
                  ))}
                </ImportTabs>

                <ExtractedDataDescription
                  dangerouslySetInnerHTML={{
                    __html: i18n.t(this.isReviewDataFlow() ? "aiImport.syncRowsDesc" : "aiImport.importRowsDesc")
                  }}
                />
              </ContentContainer>
              {this.state.showDismissConfirmation && (
                <ConfirmationDialog
                  canUserDismiss={false}
                  title={i18n.t("aiImport.abort")}
                  description={i18n.t("areYouSure")}
                  positiveButtonTitle={i18n.t("abort")}
                  negativeButtonTitle={i18n.t("stay")}
                  handlePositiveButtonClick={e => this.handleOverlayDismiss(false)}
                  handleNegativeButtonClick={e => this.setState({ showDismissConfirmation: false })}
                />
              )}
            </Container>
          </ImportDialog>
        </DialogOverlay>
      );
    }

    if ((this.state.files || this.props.documentId) && this.state.isUploading) {
      return (
        <DialogOverlay onDismiss={this.handleOverlayDismiss}>
          <ImportDialog>
            <Container>
              <ContentContainer>
                <HeaderContainer>
                  <SubTitleContainer>
                    <SubTitle>{this.getSubtitle()}</SubTitle>
                    <Title>{this.getHeaderTitle()}</Title>
                  </SubTitleContainer>
                  <AbortButton onClick={() => this.handleOverlayDismiss(true)} title={i18n.t("abort")} />
                </HeaderContainer>
                <UploadLoader
                  initialMessage={i18n.t("aiImport.waitMessage")}
                  footerMessage={i18n.t("aiImport.waitHint")}
                />
              </ContentContainer>
              {this.state.showDismissConfirmation && (
                <ConfirmationDialog
                  canUserDismiss={false}
                  title={i18n.t("aiImport.abort")}
                  description={i18n.t("areYouSure")}
                  positiveButtonTitle={i18n.t("abort")}
                  negativeButtonTitle={i18n.t("stay")}
                  handlePositiveButtonClick={e => this.handleOverlayDismiss(false)}
                  handleNegativeButtonClick={e => this.setState({ showDismissConfirmation: false })}
                />
              )}
            </Container>
          </ImportDialog>
        </DialogOverlay>
      );
    }

    return (
      <DialogOverlay onDismiss={() => this.handleOverlayDismiss(false)}>
        <ImportDialog>
          <Container>
            <DocumentUploadContainer
              onDragStart={this.handleDragStart}
              onDragStop={this.handleDragStop}
              onFileDrop={this.handleFileUpload}
              disabled={this.props.isReadOnly === true}
            >
              <CommandArea isDragging={this.state.isDragging}>
                <ContentContainer>
                  {!this.state.error === true && (
                    <>
                      <Title>{this.getTitleBasedOnContext()}</Title>
                      <Description
                        maxWidth="800px"
                        dangerouslySetInnerHTML={{
                          __html: this.getDescriptionBasedOnContext()
                        }}
                      />
                      <UploadButtonContainer margin={"44px 0px 0px 0px"}>
                        <UploadButton label={i18n.t("uploadFile")} multiple={false} onChange={this.handleFileUpload} />
                        <UploadHint>{i18n.t("uploadFileDrop")}</UploadHint>
                      </UploadButtonContainer>
                    </>
                  )}
                  {!this.state.error === false && (
                    <>
                      {this.state.error === FILE_UNSUPPORTED_ERROR ? (
                        <AiDocumentImportIconComponent />
                      ) : (
                        <AiDocumentImportErrorIconComponent />
                      )}
                      <SubTitle>{this.getSubtitle()}</SubTitle>
                      <Title>{this.getErrorTitle(this.state.error)}</Title>
                      {this.state.error !== FILE_UNSUPPORTED_ERROR && this.state.files && (
                        <ErrorPreview
                          onClick={e => {
                            const fileWindow = window.open(URL.createObjectURL(this.state.files[0]), "_blank");
                            const name = this.state.files[0].name;
                            fileWindow.addEventListener("load", function() {
                              fileWindow.document.title = name;
                            });
                          }}
                        >
                          {this.state.files[0].name}
                        </ErrorPreview>
                      )}
                      <Description
                        dangerouslySetInnerHTML={{
                          __html: this.getErrorDescription(this.state.error)
                        }}
                      />
                      <ActionButtonsContainer>
                        <UploadButtonContainer margin={"0px 0px 0px 0px"}>
                          <UploadButton
                            label={
                              this.state.error === FILE_UNSUPPORTED_ERROR
                                ? i18n.t("uploadFile")
                                : i18n.t("uploadAnotherFile")
                            }
                            multiple={false}
                            onChange={this.handleFileUpload}
                          />
                          <UploadHint>{i18n.t("uploadFileDrop")}</UploadHint>
                        </UploadButtonContainer>
                        {this.state.error !== FILE_UNSUPPORTED_ERROR && (
                          <RescanButton onClick={this.handleRescanButtonClick} title={i18n.t("rescan")} />
                        )}
                      </ActionButtonsContainer>
                    </>
                  )}
                </ContentContainer>
                <DropAreaIndicator isDragging={this.state.isDragging}>
                  <DropAreaHint>{i18n.t("aiImportDropFileHint")}</DropAreaHint>
                </DropAreaIndicator>
              </CommandArea>
            </DocumentUploadContainer>
          </Container>
        </ImportDialog>
      </DialogOverlay>
    );
  }
}

const mapStateToProps = (state, props) => {
  const targetCustodian = custodianSelector(state, props.targetCustodianId);
  return {
    currentPortfolio: currentPortfolioSelector(state),
    userPreferences: userPreferencesSelector(state),
    aiSupportedFileTypes: aiSupportedFileTypesSelector(state),
    targetCustodian,
    canShowCommittedCapital: targetCustodian ? canShowCommittedCapital(state, targetCustodian) : false
  };
};

const mapDispatchToProps = {
  uploadAiImportDocument,
  getAiImportExtractedData,
  rescanAiImportDocument
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(AiDocumentImportComponent));
